import React, { useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { CheckIcon, ChevronLeftIcon } from "@heroicons/react/20/solid";
import { BookingFlowProvider, useBookingFlow } from "../contexts/BookingFlowContext";
import PublicBookingLayout from "../components/PublicBooking/PublicBookingLayout";
import PublicBookingHero from "../components/PublicBooking/PublicBookingHero";
import PublicServiceList from "../components/PublicBooking/PublicServiceList";
import BookingSidebarGroup from "../components/PublicBooking/BookingSidebarGroup";
import AppointmentSetupStep from "../components/PublicBooking/AppointmentSetupStep";
import ContactConfirmationStep from "../components/PublicBooking/ContactConfirmationStep";
import PoliciesLink from "../components/PublicBooking/PoliciesLink";
import NotFoundPage from "./NotFoundPage";
import BookingConfirmationPage from "../components/PublicBooking/BookingConfirmationPage";
import { getBookingPageTheme } from "../utility/themeUtils";
import { getBackButtonText } from "../utility/bookingFlowUtils";
import PublicBookingPageSkeleton from "../components/Shared/LoadingSkeletons/PublicBookingPageSkeleton";

// Two layout modes share the same PublicBookingPage shell.
//
// - 'single' (services step, empty sidebar): one centered column so the
//    services list sits optically centered under the progress indicator.
// - 'pair' (any step with sidebar content): a pair-centered flex row —
//    left column sized to its content via a per-step max-width cap, fixed
//    360px sidebar, 32px gutter, centered as a pair on the viewport. Keeps
//    the sidebar's position stable across step transitions and avoids the
//    dead stripe that a percentage-sized grid track would produce.
//
// Per-step left-column max-width: chosen from what each step's content
// actually uses. The services list and contact form are comfortable at
// max-w-2xl; the time-slot grid on appointment-setup benefits from
// max-w-3xl so its lg:grid-cols-6 cells read comfortably.
const LEFT_MAX_W_BY_STEP = {
  'services': 'lg:max-w-2xl',
  'appointment-setup': 'lg:max-w-3xl',
  'contact-confirmation': 'lg:max-w-2xl',
};

const getLayoutClasses = (sidebarHasContent, currentBookingStep) => {
  if (!sidebarHasContent) {
    return {
      wrapperClass: 'min-h-screen lg:min-h-0',
      leftClass: 'max-w-2xl mx-auto w-full flex flex-col space-y-8',
    };
  }
  const leftMaxW = LEFT_MAX_W_BY_STEP[currentBookingStep] || 'lg:max-w-2xl';
  // Left column is allowed to shrink (no flex-shrink-0) so the pair stays
  // side-by-side on narrower desktop viewports (e.g. 1024px) where
  // max-w-2xl/3xl + a 360px sidebar + gutter + padding would otherwise
  // overflow and wrap. `min-w-0` prevents flex items from refusing to
  // shrink below their content's intrinsic size. The sidebar keeps its
  // fixed 360px via its own `lg:flex-shrink-0` in BookingSidebarGroup.
  //
  // NOTE: we intentionally do NOT set `lg:items-start` here — the default
  // `items-stretch` makes the sidebar flex track stretch vertically to
  // match the left column, which gives the sticky sidebar inside it room
  // to actually stick. With `items-start` the sidebar track would collapse
  // to its content height and `position: sticky` would have no slack.
  return {
    wrapperClass:
      'flex flex-col lg:flex-row lg:justify-center gap-8 min-h-screen lg:min-h-0',
    leftClass: `w-full ${leftMaxW} lg:min-w-0 flex flex-col space-y-8`,
  };
};

const PublicBookingPage = () => {
  const { slug } = useParams();

  return (
    <BookingFlowProvider slug={slug}>
      <PublicBookingPageContent />
    </BookingFlowProvider>
  );
};

const PublicBookingPageContent = () => {
  const {
    // Query state
    isLoading,
    error,
    isError,
    // Booking page data
    bookingPage,
    services,
    // Wizard state
    currentBookingStep,
    selectedServices,
    // Handlers
    handleBackToPreviousStep,
    // Submission result
    bookingResult,
  } = useBookingFlow();

  // Jump to the top instantly on any forward step change (services is the entry
  // step and is already at the top). Instant — not smooth — so the page is
  // simply at the top when the next step mounts, with no visible auto-scroll.
  useEffect(() => {
    if (currentBookingStep !== "services") {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [currentBookingStep]);

  useEffect(() => {
    if (bookingResult) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [bookingResult]);

  // Back navigation wrapper — jump to the top instantly, matching forward
  // navigation so back never animates a scroll either.
  const onBack = useCallback(() => {
    handleBackToPreviousStep();
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [handleBackToPreviousStep]);

  if (isLoading) {
    return <PublicBookingPageSkeleton />;
  }

  if (!bookingPage && !isLoading && !isError) {
    // API succeeded but returned no bookingPage object — treat as not found
    // to prevent getBookingPageTheme crashing on a null argument below.
    return <NotFoundPage />;
  }

  if (isError) {
    if (error?.message === "Booking page not found") {
      return <NotFoundPage />;
    }

    const errorTheme = getBookingPageTheme(bookingPage);
    return (
      <PublicBookingLayout
        primaryColor={errorTheme.primaryColor}
        themeMode={errorTheme.themeMode}
      >
        <div className="py-8">
          <div className="bg-themed-card border-themed rounded-lg shadow-sm border p-6 text-center">
            <div className="text-signal-red-500 mb-4">
              <svg
                className="w-12 h-12 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-themed-primary mb-2">
              Something went wrong
            </h3>
            <p className="text-themed-secondary">
              We're having trouble loading this booking page. Please try again
              later.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-themed-primary bg-themed-card border border-themed rounded-md hover:bg-opacity-80 transition-colors duration-200"
            >
              Try again
            </button>
          </div>
        </div>
      </PublicBookingLayout>
    );
  }

  const { primaryColor, themeMode } = getBookingPageTheme(bookingPage);

  // Show full-page confirmation after successful booking
  if (bookingResult) {
    return (
      <PublicBookingLayout primaryColor={primaryColor} themeMode={themeMode}>
        <BookingConfirmationPage />
      </PublicBookingLayout>
    );
  }

  // Check if booking page is inactive
  if (bookingPage.isActive === false) {
    return (
      <PublicBookingLayout primaryColor={primaryColor} themeMode={themeMode}>
        <div className="py-8">
          <div className="bg-themed-card border-themed rounded-lg shadow-sm border p-8 text-center max-w-2xl mx-auto">
            <div className="text-ink-400 mb-6">
              <svg
                className="w-16 h-16 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 7V3a1 1 0 011-1h6a1 1 0 011 1v4m-4 8V9m0 6h.01M19 9v10a2 2 0 01-2 2H7a2 2 0 01-2-2V9a2 2 0 012-2h10a2 2 0 012 2z"
                />
              </svg>
            </div>
            <h3 className="text-2xl font-semibold text-themed-primary mb-4">
              Booking Temporarily Unavailable
            </h3>
            <p className="text-themed-secondary mb-6 leading-relaxed">
              Online booking is currently unavailable. Contact us directly to schedule.
            </p>
            {bookingPage.showContactInfo &&
              (bookingPage.contactPhone || bookingPage.contactEmail) && (
                <div className="mt-6 p-4 bg-themed-card border border-themed rounded-lg">
                  <p className="text-sm font-medium text-themed-primary mb-2">
                    Contact Information
                  </p>
                  <div className="space-y-1">
                    {bookingPage.contactPhone && (
                      <p className="text-sm text-themed-secondary">
                        Phone: {bookingPage.contactPhone}
                      </p>
                    )}
                    {bookingPage.contactEmail && (
                      <p className="text-sm text-themed-secondary">
                        Email: {bookingPage.contactEmail}
                      </p>
                    )}
                  </div>
                </div>
              )}
          </div>
        </div>
      </PublicBookingLayout>
    );
  }

  // Check if no services are available
  if (!services || services.length === 0) {
    return (
      <PublicBookingLayout primaryColor={primaryColor} themeMode={themeMode}>
        <div className="py-8">
          <div className="bg-themed-card border-themed rounded-lg shadow-sm border p-8 text-center max-w-2xl mx-auto">
            <div className="text-ink-400 mb-6">
              <svg
                className="w-16 h-16 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 7v3a4.5 4.5 0 004.5 4.5H20m-12-3v9l4-4 4 4V8.5a2.5 2.5 0 00-2.5-2.5H9a1 1 0 00-1 1z"
                />
              </svg>
            </div>
            <h3 className="text-2xl font-semibold text-themed-primary mb-4">
              No Services Available
            </h3>
            <p className="text-themed-secondary mb-6 leading-relaxed">
              No services are available for online booking at this time. Contact us directly to schedule.
            </p>
            {bookingPage.showContactInfo &&
              (bookingPage.contactPhone || bookingPage.contactEmail) && (
                <div className="mt-6 p-4 bg-themed-card border border-themed rounded-lg">
                  <p className="text-sm font-medium text-themed-primary mb-2">
                    Contact Information
                  </p>
                  <div className="space-y-1">
                    {bookingPage.contactPhone && (
                      <p className="text-sm text-themed-secondary">
                        Phone: {bookingPage.contactPhone}
                      </p>
                    )}
                    {bookingPage.contactEmail && (
                      <p className="text-sm text-themed-secondary">
                        Email: {bookingPage.contactEmail}
                      </p>
                    )}
                  </div>
                </div>
              )}
          </div>
        </div>
      </PublicBookingLayout>
    );
  }

  // Helper flags for content availability
  const hasBanner = bookingPage?.bannerImageUrl;
  const hasHeroContent =
    hasBanner ||
    bookingPage?.logoUrl ||
    bookingPage?.profilePhotoUrl ||
    bookingPage?.businessName ||
    bookingPage?.providerName ||
    bookingPage?.providerBio ||
    bookingPage?.showContactInfo ||
    (bookingPage?.showSocialMedia && bookingPage.socialMediaLinks);

  const steps = [
    { id: 'services', label: 'Services' },
    { id: 'appointment-setup', label: 'Schedule' },
    { id: 'contact-confirmation', label: 'Confirm' },
  ];
  const currentStepIndex = steps.findIndex(s => s.id === currentBookingStep);

  // On the services step the business identity is now in the hero card (left
  // column), so the right sidebar only has content once a service is selected.
  // Later steps always show the booking summary in the sidebar.
  const sidebarHasContent =
    currentBookingStep !== 'services' || selectedServices.length > 0;

  const { wrapperClass, leftClass } = getLayoutClasses(
    sidebarHasContent,
    currentBookingStep,
  );

  return (
    <PublicBookingLayout primaryColor={primaryColor} themeMode={themeMode}>
      {/* Adaptive layout. Three modes: single centered column (services
          step, empty sidebar), pair-centered flex row (services step with
          sidebar populated), or the 7/3 grid (later steps). See
          getLayoutClasses for rationale. */}
      <div className={wrapperClass}>
        {/* Left Column — main content */}
        <div className={leftClass}>
          {/* Mobile-only sticky top bar — back chevron on the left, compact
              step indicator on the right. Stays visible while scrolling through
              long step content. Hidden on desktop where the inline button and
              full stepper below handle the same job. */}
          {currentBookingStep !== "services" && (
            <div className="sticky top-0 z-30 bg-themed-page-translucent border-b border-themed -mx-4 px-4 py-2.5 flex items-center justify-between lg:hidden">
              <button
                type="button"
                onClick={onBack}
                aria-label="Go to previous step"
                className="text-themed-primary px-2 py-2 -ml-2 inline-flex items-center gap-1 text-sm font-medium hover:opacity-80 focus:outline-none focus-themed"
              >
                <ChevronLeftIcon className="w-4 h-4" aria-hidden="true" />
                {getBackButtonText(currentBookingStep)}
              </button>
              <span className="text-themed-secondary text-xs tabular" aria-live="polite" aria-atomic="true">
                Step {currentStepIndex + 1} of {steps.length} &middot; {steps[currentStepIndex]?.label}
              </span>
            </div>
          )}

          {/* Step back button — desktop only; mobile uses the sticky bar above */}
          {currentBookingStep !== "services" && (
            <button
              type="button"
              onClick={onBack}
              className="hidden lg:inline-flex self-start items-center px-4 py-2 text-sm font-medium text-themed-primary bg-themed-card border border-themed rounded-pill hover:bg-opacity-80 focus:outline-none focus-themed transition-colors duration-200"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              {getBackButtonText(currentBookingStep)}
            </button>
          )}

          {currentBookingStep === "services" && hasHeroContent && (
            <PublicBookingHero
              bookingPage={bookingPage}
              primaryColor={primaryColor}
            />
          )}

          {/* Step Progress Indicator — desktop only; mobile uses the sticky bar */}
          <div className="hidden lg:flex items-center justify-center space-x-2">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex items-center space-x-1.5">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                      index < currentStepIndex
                        ? ''
                        : index === currentStepIndex
                        ? 'text-white'
                        : 'bg-themed-card-elevated text-themed-secondary border border-themed'
                    }`}
                    style={
                      index < currentStepIndex
                        ? { backgroundColor: 'var(--theme-primary-soft)', color: 'var(--theme-primary)' }
                        : index === currentStepIndex
                        ? { backgroundColor: 'var(--theme-primary)' }
                        : {}
                    }
                  >
                    {index < currentStepIndex ? (
                      <CheckIcon className="w-3.5 h-3.5" aria-hidden="true" />
                    ) : (
                      index + 1
                    )}
                  </div>
                  <span className={`text-sm font-medium ${index === currentStepIndex ? 'text-themed-primary' : 'text-themed-secondary'}`}>
                    {step.label}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className="h-px w-8"
                    style={{
                      backgroundColor:
                        index < currentStepIndex ? 'var(--theme-primary)' : 'var(--theme-border)',
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          <MainStepContent />
        </div>

        <BookingSidebarGroup />

      </div>
    </PublicBookingLayout>
  );
};

/**
 * Renders the current step's main content area.
 * Extracted to reduce the size of PublicBookingPageContent.
 */
const MainStepContent = () => {
  const {
    currentBookingStep,
    services,
    bookingPage,
    serviceLocationTypes,
    currency,
    getServiceQuantity,
    updateServiceQuantity,
    selectedServices,
    contactFormData,
    servicesRef,
    contactFormRef,
  } = useBookingFlow();

  const { primaryColor, themeMode } = getBookingPageTheme(bookingPage);

  return (
    <div className="flex-1" ref={servicesRef}>
      {currentBookingStep === "services" ? (
        <>
          <PublicServiceList
            services={services}
            getServiceQuantity={getServiceQuantity}
            onServiceQuantityChange={updateServiceQuantity}
            serviceLocationTypes={serviceLocationTypes}
            selectedServices={selectedServices}
            showLocationBadges={true}
            businessName={bookingPage?.businessName}
            currency={currency}
            primaryColor={primaryColor}
            themeMode={themeMode}
          />
          <PoliciesLink bookingPage={bookingPage} className="mt-8" />
        </>
      ) : currentBookingStep === "appointment-setup" ? (
        <AppointmentSetupStep />
      ) : currentBookingStep === "contact-confirmation" ? (
        <ContactConfirmationStep
          ref={contactFormRef}
          bookingPage={bookingPage}
          primaryColor={primaryColor}
          themeMode={themeMode}
          contactFormData={contactFormData}
        />
      ) : null}
    </div>
  );
};

export default PublicBookingPage;
